( $ => {

  // Pure per-slide layout maths (kept free of DOM access).
  //
  // i: slide index, position: animated (float) position, n: slide count, peek: slides shown right of the active one.
  // Returns x in slide-widths, opacity 0..1 and a stacking value (larger = on top).
  //
  // The circular delta d = (i - position) is mapped into [lo, lo + n); the "seam" where a slide
  // wraps from one end to the other sits at d = lo. For n >= peek + 3 there is a hidden gap
  // (d <= -1 or d >= peek + 1) and the seam is centred in it. For smaller counts there is no
  // gap, so the seam is placed at d = -1 (where opacity is already 0 behind the active slide)
  // and opacity also fades to 0 as d approaches lo + n at the right end. A slide therefore only
  // ever wraps while fully transparent. Consequence: n = 4 still shows 2 peek slides.
  // With n <= peek + 1 every slide is visible at rest, so the seam moves to d = -0.5 and both
  // fades are squeezed into half a step: the slide swapping sides fades out on the stack and
  // back in at the far right.
  // <slide-state>
  const PEEK = 2;

  const slide_state = ( i, position, n, peek = PEEK ) => {
    const squeeze = n <= peek + 1;
    const lo = squeeze ? -0.5 : -1 - Math.max( 0, n - ( peek + 2 ) ) / 2;
    const fade = squeeze ? 0.5 : 1;
    let d = ( i - position - lo ) % n;
    if ( d < 0 ) d += n;
    d += lo;

    let x = 0;
    let opacity = 0;
    if ( d <= -1 || d >= peek + 1 ) {
      opacity = 0;
    } else if ( d <= 0 ) {
      opacity = 1 + d;
    } else {
      x = d;
      opacity = d <= peek ? 1 : peek + 1 - d;
    }
    // fade out towards the seam on both sides when there is no hidden gap
    opacity = Math.max( 0, Math.min( opacity, ( d - lo ) / fade, ( lo + n - d ) / fade ) );
    return { x, opacity, d, z: Math.round( ( d - lo ) * 100 ) };
  };
  // </slide-state>

  const mod = ( a, n ) => ( ( a % n ) + n ) % n;

  class VideoSliderSection {

    static initializedClass = 'initialized';

    // spring stiffness (rad/s) of the critically damped spring; ~370ms to settle a single step
    static omega = 16;
    // px of horizontal travel before a pointer gesture becomes a drag
    static dragThreshold = 10;
    // px/ms release velocity counted as a flick
    static flickVelocity = 0.4;
    // px at the bottom of a video with native controls where gestures are left to the controls
    static controlBarHeight = 56;

    static init() {
      $( `.video-slider-section:not(.${this.initializedClass})` ).each( ( index, element ) => new this( element ) );
    }

    constructor ( section ) {
      this.$section = $( section );
      this.slider = this.$section.find('.slider').get(0);
      if ( !this.slider ) {
        return;
      }
      this.$section.addClass( this.constructor.initializedClass );

      this.slides = Array.from( this.slider.querySelectorAll(':scope > .video-slider-item') );
      this.count = this.slides.length;
      // a single slide is shown as-is by the pre-init CSS; nothing to slide
      if ( this.count < 2 ) {
        return;
      }
      this.target = 0;
      this.position = 0;
      this.velocity = 0;
      this.step = 0;
      this.raf = null;
      this.lastTime = 0;
      this.active = 0;
      this.states = [];
      this.pointer = null;
      this.reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );

      this.slider.setAttribute( 'role', 'region' );
      this.slider.setAttribute( 'aria-roledescription', 'carousel' );

      this.bind();
      this.measure();
      this.render();
      this.slider.classList.add( 'is-ready' );
    }

    bind () {
      const slider = this.slider;
      this.$section.find('.slider-prev').on( 'click', () => this.go( -1 ) );
      this.$section.find('.slider-next').on( 'click', () => this.go( 1 ) );

      slider.setAttribute( 'tabindex', '0' );
      slider.addEventListener( 'keydown', e => {
        // leave arrow keys alone for video scrubbing
        if ( e.target instanceof HTMLVideoElement ) return;
        if ( e.key === 'ArrowLeft' ) {
          e.preventDefault();
          this.go( -1 );
        } else if ( e.key === 'ArrowRight' ) {
          e.preventDefault();
          this.go( 1 );
        }
      });

      // A video started in a non-active slide (e.g. a peek slide) brings that slide to the front
      slider.addEventListener( 'playing', e => {
        const video = e.target;
        if ( !( video instanceof HTMLVideoElement ) ) return;
        const index = this.slides.indexOf( video.closest('.video-slider-item') );
        if ( index < 0 || index === mod( this.target, this.count ) ) return;

        // nearest integer t with t mod n == index (shortest direction)
        let offset = mod( index - this.target, this.count );
        if ( offset > this.count / 2 ) offset -= this.count;
        this.set_target( this.target + offset );
      }, true );

      slider.addEventListener( 'dragstart', e => e.preventDefault() );
      slider.addEventListener( 'pointerdown', e => this.pointer_down( e ) );
      slider.addEventListener( 'pointermove', e => this.pointer_move( e ) );
      slider.addEventListener( 'pointerup', e => this.pointer_end( e ) );
      slider.addEventListener( 'pointercancel', e => this.pointer_end( e ) );

      if ( 'ResizeObserver' in window ) {
        new ResizeObserver( () => {
          this.measure();
          this.render();
        }).observe( slider );
      } else {
        window.addEventListener( 'resize', () => {
          this.measure();
          this.render();
        });
      }
    }

    measure () {
      // every slide is as wide as the slider's content box
      this.step = this.slides[0].offsetWidth;
    }

    go ( delta ) {
      this.set_target( this.target + delta );
    }

    set_target ( target ) {
      this.target = target;
      if ( this.reducedMotion.matches ) {
        this.position = target;
        this.velocity = 0;
        this.stop();
        this.render();
        return;
      }
      this.start();
    }

    start () {
      if ( this.raf !== null ) return;
      this.lastTime = performance.now();
      this.raf = requestAnimationFrame( t => this.tick( t ) );
    }

    stop () {
      if ( this.raf !== null ) cancelAnimationFrame( this.raf );
      this.raf = null;
    }

    // Critically damped spring, solved analytically so it is frame-rate independent.
    // Retargeting mid-move keeps position and velocity, so rapid clicks stay smooth.
    tick ( now ) {
      const dt = Math.min( Math.max( ( now - this.lastTime ) / 1000, 0 ), 0.05 );
      this.lastTime = now;

      const w = this.constructor.omega;
      const e = this.position - this.target;
      const c = this.velocity + w * e;
      const decay = Math.exp( -w * dt );
      this.position = this.target + ( e + c * dt ) * decay;
      this.velocity = ( this.velocity - w * c * dt ) * decay;

      if ( Math.abs( this.position - this.target ) < 0.0005 && Math.abs( this.velocity ) < 0.005 ) {
        this.position = this.target;
        this.velocity = 0;
        this.raf = null;
      } else {
        this.raf = requestAnimationFrame( t => this.tick( t ) );
      }
      this.render();
    }

    render () {
      const n = this.count;
      const active = mod( Math.round( this.position ), n );
      if ( active !== this.active ) {
        this.active = active;
        this.pause_videos();
      }

      this.slides.forEach( ( slide, i ) => {
        const s = slide_state( i, this.position, n );
        slide.style.transform = `translate3d(${ ( s.x * this.step ).toFixed(2) }px,0,0)`;
        slide.style.opacity = s.opacity.toFixed(3);
        slide.style.zIndex = s.z;

        // only touch attributes when the state changes
        const state = s.opacity < 0.02 ? 'hidden' : ( s.d < -0.01 ? 'behind' : 'live' );
        if ( this.states[i] !== state ) {
          this.states[i] = state;
          const hidden = state === 'hidden';
          slide.style.visibility = hidden ? 'hidden' : '';
          slide.style.pointerEvents = state === 'live' ? '' : 'none';
          slide.inert = hidden;
          if ( hidden ) {
            slide.setAttribute( 'aria-hidden', 'true' );
          } else {
            slide.removeAttribute( 'aria-hidden' );
          }
        }
      });
    }

    // pause everything outside the active slide and the slide being travelled to
    // (a video started on a far peek slide must survive passing through intermediate slides)
    pause_videos () {
      const destination = mod( this.target, this.count );
      this.slides.forEach( ( slide, i ) => {
        if ( i === this.active || i === destination ) return;
        slide.querySelectorAll('video').forEach( video => {
          if ( !video.paused ) video.pause();
        });
      });
    }

    pointer_down ( e ) {
      if ( this.pointer || ( e.pointerType === 'mouse' && e.button !== 0 ) ) return;
      // native control bar (scrubber, volume) must keep working; the rest of the video is
      // swipeable, and plain clicks still reach it since a drag needs the threshold first
      if ( this.on_video_controls( e ) ) return;

      this.pointer = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        startPosition: this.position,
        dragging: false,
        samples: [ { t: e.timeStamp, x: e.clientX } ],
      };
    }

    // The control bar lives in the browser's shadow DOM, so approximate it as the bottom strip
    on_video_controls ( e ) {
      const video = e.target;
      if ( !( video instanceof HTMLVideoElement ) || !video.controls ) return false;
      const rect = video.getBoundingClientRect();
      const bar = Math.min( this.constructor.controlBarHeight, rect.height * .35 );
      return e.clientY > rect.bottom - bar;
    }

    pointer_move ( e ) {
      const p = this.pointer;
      if ( !p || e.pointerId !== p.id ) return;

      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;

      if ( !p.dragging ) {
        const limit = this.constructor.dragThreshold;
        if ( Math.abs( dy ) > limit && Math.abs( dy ) > Math.abs( dx ) ) {
          this.pointer = null; // vertical intent: leave it to the page scroll
          return;
        }
        if ( Math.abs( dx ) <= limit || Math.abs( dx ) <= Math.abs( dy ) ) return;

        p.dragging = true;
        // start following from where the threshold was crossed so the slide doesn't jump
        p.x = e.clientX;
        p.startPosition = this.position;
        p.samples = [];
        this.stop();
        this.velocity = 0;
        this.slider.classList.add( 'is-dragging' );
        try { this.slider.setPointerCapture( e.pointerId ); } catch ( err ) {}
      }

      p.samples.push( { t: e.timeStamp, x: e.clientX } );
      while ( p.samples.length > 2 && e.timeStamp - p.samples[0].t > 100 ) p.samples.shift();

      this.position = p.startPosition - ( e.clientX - p.x ) / this.step;
      this.render();
    }

    pointer_end ( e ) {
      const p = this.pointer;
      if ( !p || e.pointerId !== p.id ) return;
      this.pointer = null;
      if ( !p.dragging ) return; // plain click/tap: nothing to do

      this.slider.classList.remove( 'is-dragging' );
      try { this.slider.releasePointerCapture( e.pointerId ); } catch ( err ) {}

      // the click that follows a drag must not reach the play button
      const suppress = ev => {
        ev.preventDefault();
        ev.stopPropagation();
        cleanup();
      };
      const cleanup = () => {
        clearTimeout( timer );
        this.slider.removeEventListener( 'click', suppress, true );
      };
      const timer = setTimeout( cleanup, 100 );
      this.slider.addEventListener( 'click', suppress, true );

      let velocity = 0; // px/ms
      const first = p.samples[0];
      const last = p.samples[ p.samples.length - 1 ];
      if ( first && last && last.t > first.t ) {
        velocity = ( last.x - first.x ) / ( last.t - first.t );
      }

      let target = Math.round( this.position );
      if ( e.type !== 'pointercancel' && Math.abs( velocity ) > this.constructor.flickVelocity ) {
        // dragging left (negative velocity) moves forward
        target = velocity < 0 ? Math.floor( this.position ) + 1 : Math.ceil( this.position ) - 1;
      }
      this.set_target( target );
    }

  }


  $( () => VideoSliderSection.init() )

})( jQuery );
